import { getLangBasename } from './i18n/langBasename';

let lastInAppPath: string | null = null;

/** Remember the route the visitor was on before a client-side hop to /login. */
export function rememberInAppPath(pathname: string, search: string): void {
  if (pathname === '/login') return;
  lastInAppPath = `${pathname}${search}`;
}

function pathFromReferrer(): string | null {
  if (typeof document === 'undefined' || !document.referrer) return null;
  try {
    const url = new URL(document.referrer);
    if (url.origin !== window.location.origin) return null;
    let path = url.pathname;
    const base = getLangBasename();
    if (base && (path === base || path.startsWith(`${base}/`))) {
      path = path.slice(base.length) || '/';
    }
    if (!path.startsWith('/') || path === '/login' || path.startsWith('/signin')) return null;
    return `${path}${url.search}`;
  } catch {
    return null;
  }
}

function isMcpAuthorizePath(path: string): boolean {
  return path === '/authorize' || path.startsWith('/authorize?');
}

/**
 * Where to send someone after sign-in. An MCP /authorize referrer wins: that
 * navigation is the consent round-trip, and the logged-in login page must
 * resume it instead of falling through to cart history. Otherwise client-side
 * navigation wins over the document referrer, which is the page that first
 * loaded the React app.
 */
export function loginReturnPath(): string | null {
  const referrer = pathFromReferrer();
  if (referrer && isMcpAuthorizePath(referrer)) return referrer;
  return lastInAppPath || referrer;
}

/**
 * In-app page to attach to the sign-in request. /authorize is not included:
 * the httpOnly returnTo cookie already carries the validated OAuth URL, and
 * the document referrer is a decoded URL (it contains ://) that must not
 * replace that cookie.
 */
export function signInReturnPath(): string | null {
  const path = loginReturnPath();
  if (!path || isMcpAuthorizePath(path)) return null;
  return path;
}

export function providerSignInHref(provider: string, returnPath: string | null): string {
  if (!returnPath || isMcpAuthorizePath(returnPath)) return `/signin/${provider}`;
  return `/signin/${provider}?return=${encodeURIComponent(returnPath)}`;
}
